import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import ShortenedLinks from './components/ShortenedLinks';
import RedirectPage from './components/RedirectPage';
import HomePage from './components/HomePage';
import LoginPage from './components/LoginPage';
import SignUpPage from './components/SignUpPage';
import New_Dashboard from './components/New_Dashboard';
import NewDashboard from './components/newDB';
import DocumentationPage from './components/DocumentationPage';
import AboutPage from './components/AboutPage';
import FeaturesPage from './components/FeaturesPage';
import SupportPage from './components/SupportPage';
import PricingPage from './components/PricingPage';
import PrivacyPage from './components/PrivacyPage';
import TermsPage from './components/TermsPage';
import NotFoundPage from './components/NotFoundPage';
import ProfilePage from './components/ProfilePage';
import Navigation from './components/Navbar';
import Footer from './components/Footer';
import { AuthProvider } from './AuthProvider';
import './css/home.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import "./index.css";
import LinkDetails from './components/LinkDetails';
import NewsPage from './components/NewsPage';
import NewsPostPage from './components/NewsPostPage';
import BlogPage from './components/BlogPage';
import BlogPostPage from './components/BlogPostPage';
import LinksDataPage from './components/LinksDataPage';

const setMetaTag = (attr, key, value) => {
  if (!value) return;
  let tag = document.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
};

const setLinkTag = (rel, href) => {
  if (!href) return;
  let tag = document.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
};

const getStaticMeta = (pathname) => {
  const clean = pathname || '/';
  const map = {
    '/': {
      title: 'BlinkURL � Fast, secure URL shortener',
      description:
        'Shorten links instantly, track clicks in real time, and protect visitors with preview pages.',
    },
    '/about': {
      title: 'About BlinkURL',
      description:
        'Learn why BlinkURL was built and how we keep link sharing fast, secure, and transparent.',
    },
    '/features': {
      title: 'BlinkURL Features � Analytics, branded links, and previews',
      description:
        'Explore BlinkURL features including real-time analytics, safe redirect previews, and share-ready links.',
    },
    '/pricing': {
      title: 'BlinkURL Pricing',
      description:
        'See BlinkURL plans and what is included for analytics, sharing, and link management.',
    },
    '/support': {
      title: 'BlinkURL Support',
      description:
        'Get help with BlinkURL links, analytics, sharing, and account settings.',
    },
    '/documentation': {
      title: 'BlinkURL Documentation',
      description:
        'Documentation for using BlinkURL, including link creation, redirect previews, and analytics.',
    },
    '/terms': {
      title: 'BlinkURL Terms',
      description:
        'Read BlinkURL terms of service and usage rules.',
    },
    '/privacy': {
      title: 'BlinkURL Privacy',
      description:
        'Learn how BlinkURL handles privacy, data collection, and consent.',
    },
  };
  return map[clean] || null;
};

const AppLayout = () => {
  const location = useLocation();
  const path = location.pathname || '/';
  const knownRoutes = new Set([
    '/',
    '/login',
    '/signup',
    '/dashboard',
    '/new',
    '/shortened-links',
    '/documentation',
    '/about',
    '/features',
    '/support',
    '/news',
    '/blog',
    '/links/data',
    '/pricing',
    '/privacy',
    '/terms',
    '/profile',
  ]);
  const segments = path.split('/').filter(Boolean);
  const isRedirectRoute =
    segments.length === 1 &&
    !knownRoutes.has(path) &&
    !path.endsWith('/details');

  useEffect(() => {
    // Keep canonical accurate per route (static index.html is always /).
    const canonical = new URL(path, window.location.origin).href;
    setLinkTag('canonical', canonical);

    // Avoid indexing private pages and short-link previews.
    const noIndexPrefixes = [
      '/login',
      '/signup',
      '/dashboard',
      '/profile',
      '/shortened-links',
      '/new',
    ];
    const shouldNoIndex =
      isRedirectRoute ||
      path.endsWith('/details') ||
      noIndexPrefixes.includes(path);
    setMetaTag('name', 'robots', shouldNoIndex ? 'noindex, nofollow' : 'index, follow');

    // Blog and News pages manage their own titles/descriptions.
    if (path.startsWith('/blog') || path.startsWith('/news')) {
      setMetaTag('property', 'og:url', canonical);
      return;
    }

    const meta = getStaticMeta(path);
    if (meta) {
      document.title = meta.title;
      setMetaTag('name', 'description', meta.description);
      setMetaTag('property', 'og:title', meta.title);
      setMetaTag('property', 'og:description', meta.description);
      setMetaTag('property', 'og:url', canonical);
      setMetaTag('name', 'twitter:title', meta.title);
      setMetaTag('name', 'twitter:description', meta.description);
    } else {
      setMetaTag('property', 'og:url', canonical);
    }
  }, [path, isRedirectRoute]);

  return (
    <div>
      <Navigation />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/new" element={<NewDashboard />} />
        <Route path="/shortened-links" element={<ShortenedLinks />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/dashboard" element={<New_Dashboard />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/documentation" element={<DocumentationPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/news" element={<NewsPage />} />
        <Route path="/news/:slug" element={<NewsPostPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/links/data" element={<LinksDataPage />} />
        <Route path="/:shortUrl/details" element={<LinkDetails />} />
        <Route path="/:shortUrl+" element={<LinkDetails />} />
        <Route path="/:shortUrl" element={<RedirectPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      {!isRedirectRoute && (
        <button
          type="button"
          className="mobile-reload"
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      )}
      {!isRedirectRoute && <Footer />}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppLayout />
      </Router>
    </AuthProvider>
  );
}
export default App;
